import { parseRegionalBrandCode } from '@/lib/auth/domainSwitch'
import { isEuropeanCountry } from '@/lib/middleware/variantRedirect.helper'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'

export type CountryBrandLookup = {
  isoCode: string
  brandCode?: string | null
}

type CartLikeForMarketCountry = {
  items?: readonly { needsShipping?: boolean | null }[] | null
  country?: { isoCode?: string | null } | null
  shippingAddress?: { country?: { isoCode?: string | null } | null } | null
}

/**
 * ISO code used to decide the cart's regional checkout market.
 *
 * Prefer cart country because it reflects `updateShoppingCartLocation` /
 * pricing country immediately. If that is missing, fall back to ship-to for
 * shippable carts.
 */
export function checkoutCountryIsoForRegionalMarket(
  cart: CartLikeForMarketCountry | null | undefined
): string | null {
  const cartIso = cart?.country?.isoCode?.trim()
  if (cartIso) {
    return cartIso
  }
  const items = cart?.items
  const needsShipping = items?.some(i => i.needsShipping) ?? false
  const shipToIso = cart?.shippingAddress?.country?.isoCode?.trim()
  if (needsShipping && shipToIso) {
    return shipToIso
  }
  return null
}

/**
 * Which US/UK storefront should serve this cart for checkout (prices + payment rails).
 * Prefer `countryIsoCode` (callers: use {@link checkoutCountryIsoForRegionalMarket}) →
 * Countries API brandCode, then Europe list, then cart brand, then currency.
 *
 * Currency is intentionally the weakest signal here because it can lag during cart repricing
 * and should not override a known country or brand decision by itself.
 */
export function resolveCheckoutRegionalBrand(options: {
  countryIsoCode?: string | null
  cartBrandCode?: string | null
  currencyCode?: string | null
  countries: readonly CountryBrandLookup[]
}): RegionalBrandCode | null {
  const iso = options.countryIsoCode?.trim()
  if (iso) {
    const row = options.countries.find(
      c => c.isoCode?.trim().toUpperCase() === iso.toUpperCase()
    )
    const fromApi = parseRegionalBrandCode(row?.brandCode)
    if (fromApi) return fromApi
    return isEuropeanCountry(iso) ? 'VANISHING_INC_UK' : 'VANISHING_INC_US'
  }

  const fromCartBrand = parseRegionalBrandCode(options.cartBrandCode)
  if (fromCartBrand) return fromCartBrand

  const cur = options.currencyCode?.trim().toUpperCase()
  if (cur === 'GBP') return 'VANISHING_INC_UK'
  if (cur === 'USD') return 'VANISHING_INC_US'

  return null
}
